import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from 'styletron-react';
import { Button } from 'baseui/button';
import { HeadingMedium } from 'baseui/typography';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { CustomColumn, StringColumn } from 'baseui/data-table';

import { Flex } from '../../components/Flex';
import { BaseGrid, GridActions } from '../../components/BaseGrid';
import { BaseGridWithSearch } from '../../components/BaseGridWithSearch';
import { useEstates, useEstateGroups } from '../../hooks/useEstates';
import { EstateForm } from '../../components/Estate/EstateForm';
import { EstateStatus } from '../../components/EstateStatus';
import { isUserAdmin } from '../../utils/auth';
import { ArrowLeft } from 'lucide-react';
import type { Estate, EstateGroup } from '../../api/generated';

const Container = styled('div', {
  padding: '24px',
  backgroundColor: '#fff',
  minHeight: 'calc(100vh - 70px)',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

const BackButton = styled('button', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#666',
  marginBottom: '16px',
  ':hover': {
    color: '#000',
  },
});

const LoadingContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
});

export const EstateGroupDetails = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEstate, setEditingEstate] = useState<Estate | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');

  const { allEstateGroups } = useEstateGroups();
  const {
    estates,
    isLoading,
    error,
    totalPages,
    currentPage,
    totalEstates,
    createEstate,
    updateEstate,
    deleteEstate,
    isCreating,
    isUpdating,
    isDeleting
  } = useEstates({
    estateGroupId: groupId ? parseInt(groupId) : undefined,
    page,
    limit,
    ...(searchTerm && { name: searchTerm })
  });

  const currentGroup = allEstateGroups.find((group: EstateGroup) => group.id?.toString() === groupId);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const adminStatus = await isUserAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    checkAdminStatus();
  }, []);

  // Add global error handler for unhandled promise rejections
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Ignore axios CanceledError as it's expected when components unmount
      if (event.reason?.name === 'CanceledError' || event.reason?.code === 'ERR_CANCELED') {
        event.preventDefault();
        return;
      }
      
      console.error('Unhandled promise rejection in Estate Details:', event.reason);
      // Prevent the error from showing in the console as uncaught
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Handle errors from the hook
  useEffect(() => {
    if (error as any) {
      console.error('Estates Error:', error);
      toaster.negative('Error loading estates', {
        autoHideDuration: 3000,
      });
    }
  }, [error]);

  // Reset page when limit changes
  useEffect(() => {
    setPage(1);
  }, [limit]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Handle search
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleCreateEstate = (data: Estate) => {
    try {
      createEstate({
        ...data,
        estate_group_id: groupId ? parseInt(groupId) : undefined,
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error in handleCreateEstate:', error);
    }
  };

  const handleUpdateEstate = (data: Estate) => {
    try {
      if (editingEstate?.id) {
        updateEstate(editingEstate.id.toString(), data);
        setEditingEstate(null);
      }
    } catch (error) {
      console.error('Error in handleUpdateEstate:', error);
    }
  };

  const handleAction = (estate: Estate, action: string) => {
    try {
      switch (action) {
        case 'Edit':
          if (isAdmin) {
            setEditingEstate(estate);
          } else {
            toaster.negative('You do not have permission to edit estates', {
              autoHideDuration: 3000,
            });
          }
          break;
        case 'Delete':
          if (isAdmin && estate.id) {
            if (window.confirm('Are you sure you want to delete this estate?')) {
              deleteEstate(estate.id.toString());
            }
          } else {
            toaster.negative('You do not have permission to delete estates', {
              autoHideDuration: 3000,
            });
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error in handleAction:', error);
      toaster.negative('An error occurred while performing the action', {
        autoHideDuration: 3000,
      });
    }
  };

  // Transform estates to match the expected row format (like working invitations)
  const tableData = (estates || [])
    .filter((estate: Estate) => estate && estate.id !== undefined) // Filter out invalid entries
    .map((estate: Estate) => ({
      id: estate.id!,  // Use non-null assertion since we filtered out undefined ids
      data: estate, // Store the complete estate data in 'data' property like invitations
    }));

  const columns = [
    StringColumn({
      title: 'ID',
      maxWidth: 80,
      mapDataToValue: (row: any) => {
        if (!row || row.id === undefined) return '';
        return String(row.id);
      },
    }),
    StringColumn({
      title: 'Name',
      mapDataToValue: (row: any) => {
        if (!row || !row.name) return '';
        return String(row.name);
      },
    }),
    CustomColumn({
      title: 'Status',
      mapDataToValue: (row: any) => row?.status,
      renderCell: ({ value }: { value: string }) => (
        <EstateStatus row={{ value }} />
      ),
    }),
    CustomColumn({
      title: 'Actions',
      maxWidth: 120,
      mapDataToValue: (row: any) => {
        return row || null;
      },
      renderCell: ({ value }: { value: Estate }) => {
        if (!value) return null;
        return (
          <GridActions 
            value={value} 
            onAction={(estate: object, action: string) => handleAction(estate as Estate, action)}
            actions={isAdmin ? [
              { label: "Edit" },
              { label: "Delete" }
            ] : []}
          />
        );
      },
    }),
  ];

  if (error as any) {
    return (
      <Container>
        <BackButton onClick={() => navigate('/estates')}>
          ← Back to Estate Groups
        </BackButton>
        <HeadingMedium>Error loading estates</HeadingMedium>
        <p>Please try again later.</p>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/estates')}>
        ← Back to Estate Groups
      </BackButton>
      
      <Header>
        <HeadingMedium margin={0}>
          Estates in {currentGroup?.name || `Group ${groupId}`}
        </HeadingMedium>
        {isAdmin && (
          <Button
            onClick={() => setShowCreateForm(true)}
            disabled={isCreating}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #000)',
                }
              }
            }}
          >
            {isCreating ? 'Creating...' : '+ Create Estate'}
          </Button>
        )}
      </Header>

      {isLoading ? (
        <LoadingContainer>
          <Spinner $size={40} />
        </LoadingContainer>
      ) : (
        <BaseGridWithSearch
          loading={isDeleting}
          loadingMessage={"Loading estates..."}
          columns={columns}
          rows={tableData}
          emptyMessage={searchTerm ? "No estates found for your search" : "No estates available in this group"}
          searchPlaceholder="Search estates by name..."
          onSearch={handleSearch}
          pagination={{
            numPages: totalPages,
            currentPage: currentPage,
            onPageChange: ({ nextPage }) => {
              if (nextPage !== currentPage) {
                setPage(nextPage);
              }
            },
          }}
        />
      )}

      {(showCreateForm || editingEstate) && (
        <EstateForm
          isOpen={showCreateForm || !!editingEstate}
          onClose={() => {
            setShowCreateForm(false);
            setEditingEstate(null);
          }}
          onSubmit={editingEstate ? handleUpdateEstate : handleCreateEstate}
          initialData={editingEstate || undefined}
          isLoading={isCreating || isUpdating}
          title={editingEstate ? 'Edit Estate' : 'Create Estate'}
          estateGroups={allEstateGroups}
          selectedGroupId={groupId ? parseInt(groupId) : undefined}
        />
      )}
    </Container>
  );
}; 